import {
  bulkAddTag,
  bulkRemoveTag,
  createTag,
  deleteTag as deleteInboxTag,
  getWorkspaceTags,
  updateTag as updateInboxTag,
} from "../../../api/inbox-tags";
import {
  bulkUpdateInboxV2ConversationUnreadCounts,
  bulkUpdateInboxV2ConversationStarred,
  bulkUpdateInboxV2ConversationStatuses,
  bulkAssignInboxV2Conversations,
  bulkUnassignInboxV2Conversations,
  createInboxV2Board,
  createInboxV2Feed,
  createInboxV2Template,
  checkInboxV2MessageFileAttachments,
  getInboxV2ConversationsByFilters,
  refreshInboxV2MessageAttachments,
  refreshInboxV2PostPreviews,
  deleteInboxV2Board,
  deleteInboxV2Feed,
  deleteInboxV2Template,
  duplicateInboxV2Feed,
  getInboxV2Templates,
  setInboxV2BoardPinned,
  setInboxV2FeedHidden,
  getInboxV2PostCommentReplies,
  getInboxV2PostComments,
  getInboxV2ProfileComments,
  updateInboxV2Board,
  updateInboxV2BoardFeedOrder,
  updateInboxV2Feed,
  updateInboxV2Template,
} from "../../../api/inbox-v2";
import {
  deleteMessageReaction,
  setMessageReaction,
} from "../../../api/message-reaction";
import {
  createMessage as createLegacyMessage,
  getMessage as getLegacyMessage,
  setMessageDeletedStatus,
  setMessageHidden,
  setMessageSentiment,
  setMessageText,
} from "../../../api/messages";
import { getConversation, patchConversation } from "../../../api/conversations";
import { skedInboxApi } from "../../../api/_skedInboxApi";

import type {
  AddInboxV2ConversationTagInput,
  AddInboxV2MessageReactionInput,
  AssignInboxV2ConversationsInput,
  CreateInboxV2BoardInput,
  CreateInboxV2FeedInput,
  CreateInboxV2ReplyInput,
  CreateInboxV2TagInput,
  CreateInboxV2TemplateInput,
  GetInboxV2ConversationMessagesInput,
  GetInboxV2FeedConversationsInput,
  GetInboxV2PostCommentRepliesInput,
  GetInboxV2PostCommentsInput,
  GetInboxV2ProfileCommentsInput,
  InboxV2BoardPayload,
  InboxV2Account,
  InboxV2CommentRef,
  InboxV2FeedConversationsSourcePayload,
  InboxV2PostCommentRepliesPage,
  InboxV2PostCommentsPage,
  InboxV2ProfileCommentsPage,
  InboxV2Sentiment,
  RemoveInboxV2MessageReactionInput,
  RemoveInboxV2ConversationTagInput,
  UpdateInboxV2BoardFeedOrderInput,
  UpdateInboxV2BoardInput,
  SetInboxV2BoardPinnedInput,
  UpdateInboxV2ConversationInput,
  UpdateInboxV2ConversationUnreadCountsInput,
  UpdateInboxV2ConversationStarredInput,
  UpdateInboxV2ConversationStatusesInput,
  UpdateInboxV2FeedInput,
  UpdateInboxV2MessageInput,
  UpdateInboxV2TemplateInput,
  UpdateInboxV2TagInput,
  UnassignInboxV2ConversationsInput,
} from "../../types";
import {
  mapMessageToInboxV2Message,
  mapConversationToInboxV2Conversation,
  mapInboxV2BoardDtoToBoard,
  mapInboxV2FeedDtoToFeed,
  mapReactionToInboxV2Reaction,
  mapSentimentTypeToInboxV2Sentiment,
  mapTagToInboxV2Tag,
} from "../mapper";
import { inboxV2MessageFetchManager } from "./message-fetch-manager";
import { applyInboxV2DateRangeToFilters } from "../store/feed/date-range";
import type { InboxV2RawConversation, InboxV2RawMessage } from "./types";
import type { InboxV2ApiCacheOptions } from "./api-cache";
import {
  getInboxV2BoardsPayload,
  INBOX_V2_GET_BOARDS_CACHE_KEY,
  invalidateInboxV2GetBoardsCache,
} from "./board-payload-api";
import {
  buildConversationUpdateSystemMessages,
  buildReplySystemMessages,
  mapInboxV2SentimentToLegacyValue,
  mapInboxV2StatusToConversationStatus,
} from "./data-source-helpers";
import {
  getInboxV2SenderProfile,
  getInboxV2SenderProfilesApi,
} from "./sender-profile-api";

export { INBOX_V2_GET_BOARDS_CACHE_KEY, invalidateInboxV2GetBoardsCache };

export const realInboxV2DataSource = {
  async getBoardPayload() {
    const payload = await getInboxV2BoardsPayload();

    return {
      ...payload,
      conversations: [],
    } satisfies InboxV2BoardPayload;
  },

  async getBoards(options: Partial<InboxV2ApiCacheOptions> = {}) {
    return getInboxV2BoardsPayload(options);
  },

  async getTemplates() {
    const payload = await getInboxV2Templates();

    return payload.templates;
  },

  async getTags() {
    const tags = await getWorkspaceTags();
    return tags.map((tag) => mapTagToInboxV2Tag(tag));
  },

  async createTemplate(input: CreateInboxV2TemplateInput) {
    return createInboxV2Template(input);
  },

  async updateTemplate(input: UpdateInboxV2TemplateInput) {
    return updateInboxV2Template(input);
  },

  async deleteTemplate(templateId: string) {
    await deleteInboxV2Template(templateId);
  },

  async getConversation(
    conversationId: string,
    accounts: InboxV2Account[] = []
  ) {
    const { data: conversationResponse } = await getConversation({
      conversationId,
    });
    const rawConversation = conversationResponse as InboxV2RawConversation;

    return mapConversationToInboxV2Conversation(rawConversation, {
      account: accounts.find(
        (account) => account.id === rawConversation.account
      ),
      messages: [],
      messagesLoaded: false,
    });
  },

  async getMessage(
    messageId: string,
    conversationAccountId: string,
    account?: InboxV2Account
  ) {
    const { data } = await getLegacyMessage(messageId);
    const message = mapMessageToInboxV2Message(
      {
        ...(data as InboxV2RawMessage),
        // GET /messages/:id returns relation ids, not populated entities.
        // The linked private reply is outbound, so use the account already in
        // the panel instead of treating the sender ObjectId as display data.
        sender: undefined,
      },
      { account, conversationAccountId }
    );

    return {
      ...message,
      accountId: conversationAccountId,
      authorId: account?.id ?? conversationAccountId,
      authorName: account?.login || account?.name || "account",
      authorHandle: account?.login ?? account?.id ?? conversationAccountId,
      authorPlatform: account?.platform,
      avatarUrl: account?.avatarUrl,
      isBrand: true,
      isConversationAccountSender: true,
      sentFromSkedUserName: undefined,
    };
  },

  async getPrivateReplyComment(
    messageId: string,
    conversationAccountId: string,
    account?: InboxV2Account
  ) {
    const { data } = await getLegacyMessage(messageId, {
      populateSender: true,
    });
    return mapMessageToInboxV2Message(data as InboxV2RawMessage, {
      account,
      conversationAccountId,
    });
  },

  async createPrivateReply(input: {
    accountId: string;
    commentRef: InboxV2CommentRef;
    platform: "facebook" | "instagram";
    senderPlatformId: string;
    text: string;
  }) {
    const response = await skedInboxApi.post<{
      conversationId: string;
      message: InboxV2RawMessage;
    }>("/messages/private-reply", {
      ...input,
      platform: input.platform === "instagram" ? "IG" : "FB",
      closeConversation: false,
    });

    return {
      ...mapMessageToInboxV2Message(response.data.message, {
        conversationAccountId: input.accountId,
      }),
      accountId: input.accountId,
      conversationId: response.data.conversationId,
    };
  },

  async getConversationMessages(input: GetInboxV2ConversationMessagesInput) {
    return inboxV2MessageFetchManager.fetch({
      account: input.account,
      conversationId: input.conversationId,
      nextToken: input.nextToken,
    });
  },

  async createBoard(input: CreateInboxV2BoardInput) {
    return mapInboxV2BoardDtoToBoard(await createInboxV2Board(input));
  },

  async updateBoard(input: UpdateInboxV2BoardInput) {
    return mapInboxV2BoardDtoToBoard(await updateInboxV2Board(input));
  },

  async setBoardPinned(input: SetInboxV2BoardPinnedInput) {
    const board = mapInboxV2BoardDtoToBoard(await setInboxV2BoardPinned(input));

    invalidateInboxV2GetBoardsCache();

    return board;
  },

  async deleteBoard(boardId: string) {
    await deleteInboxV2Board(boardId);
  },

  async getFeedConversations(input: GetInboxV2FeedConversationsInput) {
    if (!input.feed) {
      throw new Error("Feed metadata is required to load conversations");
    }

    if (
      input.feed.filters.accountIds.match === "or" &&
      input.feed.filters.accountIds.values.length === 0
    ) {
      return {
        conversationIds: [],
        conversations: [],
        feedId: input.feedId,
        nextToken: undefined,
      } satisfies InboxV2FeedConversationsSourcePayload;
    }

    const filters = applyInboxV2DateRangeToFilters(
      input.feed.filters,
      input.dateRange
    );

    const conversationResponse = await getInboxV2ConversationsByFilters({
      filters,
      next: input.nextToken,
      limit: input.pageSize,
      messageLimit: input.messageLimit ?? 0,
    });

    const rawConversations = conversationResponse.items ?? [];
    const conversations = rawConversations.map((conversation) =>
      mapConversationToInboxV2Conversation(
        conversation as InboxV2RawConversation,
        {
          account: input.accounts?.find(
            (account) => account.id === conversation.account
          ),
        }
      )
    );

    return {
      conversationIds: conversations.map((conversation) => conversation.id),
      conversations,
      feedId: input.feedId,
      nextToken: conversationResponse.next ?? undefined,
    } satisfies InboxV2FeedConversationsSourcePayload;
  },

  async createFeed(input: CreateInboxV2FeedInput) {
    return mapInboxV2FeedDtoToFeed(await createInboxV2Feed(input));
  },

  async updateFeed(input: UpdateInboxV2FeedInput) {
    return mapInboxV2FeedDtoToFeed(await updateInboxV2Feed(input));
  },

  async deleteFeed(feedId: string) {
    await deleteInboxV2Feed(feedId);
  },

  async duplicateFeed(feedId: string) {
    return mapInboxV2FeedDtoToFeed(await duplicateInboxV2Feed(feedId));
  },

  async setFeedHidden(feedId: string, hidden: boolean) {
    return mapInboxV2FeedDtoToFeed(await setInboxV2FeedHidden(feedId, hidden));
  },

  async updateBoardFeedOrder(input: UpdateInboxV2BoardFeedOrderInput) {
    await updateInboxV2BoardFeedOrder(input);
  },

  async refreshPostPreviews(input: {
    items: Array<{
      conversationId: string;
      postId: string;
    }>;
  }) {
    const response = await refreshInboxV2PostPreviews({
      ...input,
      refreshReason: "image-load-error",
    });

    return {
      errors:
        response.errors?.map((item) => ({
          conversationId: item.conversationId,
          fetchError: item.fetchError,
          postId: item.postId,
        })) ?? [],
      items: response.items.map((conversation) => ({
        conversation: mapConversationToInboxV2Conversation(
          conversation as InboxV2RawConversation,
          {
            messages: [],
            messagesLoaded: false,
          }
        ),
        postId: conversation.post?.id ?? "",
      })),
    };
  },

  async refreshMessageAttachments(input: {
    items: Array<{
      conversationId: string;
      messageId: string;
    }>;
  }) {
    const response = await refreshInboxV2MessageAttachments({
      items: input.items.map((item) => ({
        messageId: item.messageId,
      })),
      refreshReason: "image-load-error",
    });

    return {
      errors:
        response.errors?.map((item) => ({
          fetchError: item.fetchError,
          messageId: item.messageId,
        })) ?? [],
      items: response.items.map((item) => ({
        conversationId: item.conversationId,
        message: {
          ...mapMessageToInboxV2Message(item.message as InboxV2RawMessage),
          conversationId: item.conversationId,
        },
      })),
    };
  },

  async checkMessageFileAttachments(input: {
    items: Array<{
      conversationId: string;
      messageId: string;
    }>;
  }) {
    return checkInboxV2MessageFileAttachments({
      items: input.items.map((item) => ({
        messageId: item.messageId,
      })),
    });
  },

  async createReply(input: CreateInboxV2ReplyInput) {
    if (input.privateReplyContext) {
      const message = await this.createPrivateReply({
        ...input.privateReplyContext,
        text: input.text,
      });

      return {
        confirmedSystemMessages: [],
        conversation: undefined,
        statusSnapshot: undefined,
        message,
      };
    }

    const currentConversation = await this.getConversation(
      input.conversationId
    );
    const response = await createLegacyMessage({
      conversationId: input.conversationId,
      text: input.text,
      isInternal: input.isInternal,
      closeConversationAction: input.closeConversation
        ? "remove-conversation"
        : undefined,
      replyToId: input.replyToId,
      attachments: input.attachments,
    });

    const message = {
      ...mapMessageToInboxV2Message({
        ...(response.data as InboxV2RawMessage),
        reactions: response.data.reactions ?? [],
      }),
      conversationId: input.conversationId,
    };

    return {
      statusSnapshot: response.data.statusSnapshot
        ? {
            status:
              response.data.statusSnapshot.status === "CLOSED"
                ? ("closed" as const)
                : ("open" as const),
            statusRevision: response.data.statusSnapshot.statusRevision,
          }
        : undefined,
      confirmedSystemMessages: buildReplySystemMessages({
        conversation: currentConversation,
        input,
        message,
      }),
      message,
    };
  },

  async addMessageReaction(input: AddInboxV2MessageReactionInput) {
    const response = await setMessageReaction({
      messageId: input.messageId,
      reaction: input.reaction,
    });

    return mapReactionToInboxV2Reaction(response.data);
  },

  async removeMessageReaction(input: RemoveInboxV2MessageReactionInput) {
    await deleteMessageReaction({
      messageId: input.messageId,
      reactionId: input.reactionId,
    });
  },

  async updateConversation(input: UpdateInboxV2ConversationInput) {
    const currentConversation = await this.getConversation(input.id);
    const nextTagIds = input.tagIds ?? currentConversation.tagIds;
    const currentTagIds = currentConversation.tagIds;

    const tagsToAdd = nextTagIds.filter(
      (tagId) => !currentTagIds.includes(tagId)
    );
    const tagsToRemove = currentTagIds.filter(
      (tagId) => !nextTagIds.includes(tagId)
    );
    const shouldPatchConversation =
      input.assigneeId !== undefined ||
      input.indicators !== undefined ||
      input.status !== undefined;

    await Promise.all([
      ...(shouldPatchConversation
        ? [
            patchConversation({
              conversationId: input.id,
              ...(input.assigneeId !== undefined
                ? { assignedTo: input.assigneeId }
                : {}),
              ...(input.indicators !== undefined
                ? { isStarred: input.indicators.includes("bookmark") }
                : {}),
              ...(input.status !== undefined
                ? {
                    status: mapInboxV2StatusToConversationStatus(input.status),
                  }
                : {}),
            }),
          ]
        : []),
      ...tagsToAdd.map((tagId) => bulkAddTag(tagId, [input.id])),
      ...tagsToRemove.map((tagId) => bulkRemoveTag(tagId, [input.id])),
    ]);

    const updatedConversation = await this.getConversation(input.id);

    return {
      confirmedSystemMessages: buildConversationUpdateSystemMessages({
        currentConversation,
        input,
        updatedConversation,
      }),
      conversation: updatedConversation,
    };
  },

  async updateConversationUnreadCounts(
    input: UpdateInboxV2ConversationUnreadCountsInput
  ) {
    return bulkUpdateInboxV2ConversationUnreadCounts(input);
  },

  async updateConversationStarred(
    input: UpdateInboxV2ConversationStarredInput
  ) {
    return bulkUpdateInboxV2ConversationStarred(input);
  },

  async updateConversationStatuses(
    input: UpdateInboxV2ConversationStatusesInput
  ) {
    const response = await bulkUpdateInboxV2ConversationStatuses({
      ...input,
      status: input.status === "closed" ? "CLOSED" : "OPEN",
    });

    return {
      results: response.results.map((result) => {
        const statusSnapshot = result.statusSnapshot
          ? {
              status:
                result.statusSnapshot.status === "CLOSED"
                  ? ("closed" as const)
                  : ("open" as const),
              statusRevision: result.statusSnapshot.statusRevision,
            }
          : undefined;
        return result.state === "updated"
          ? {
              ...result,
              statusSnapshot,
              systemMessages: result.systemMessages.map((message) => ({
                ...mapMessageToInboxV2Message(message),
                conversationId: result.conversationIds,
              })),
            }
          : { ...result, statusSnapshot };
      }),
    };
  },

  async addConversationTag(input: AddInboxV2ConversationTagInput) {
    return bulkAddTag(input.tagId, input.conversationIds);
  },

  async removeConversationTag(input: RemoveInboxV2ConversationTagInput) {
    return bulkRemoveTag(input.tagId, input.conversationIds);
  },

  async assignConversations(input: AssignInboxV2ConversationsInput) {
    const response = await bulkAssignInboxV2Conversations(input);

    return {
      results: response.results.map((result) =>
        result.state === "updated"
          ? {
              ...result,
              systemMessages: result.systemMessages.map((message) => ({
                ...mapMessageToInboxV2Message(message),
                conversationId: result.conversationIds,
              })),
            }
          : result
      ),
    };
  },

  async unassignConversations(input: UnassignInboxV2ConversationsInput) {
    return bulkUnassignInboxV2Conversations(input);
  },

  async updateMessage(input: UpdateInboxV2MessageInput) {
    let conversationSentimentType: InboxV2Sentiment | undefined;

    if (input.sentiment !== undefined) {
      const { data } = await setMessageSentiment({
        id: input.messageId,
        sentiment: mapInboxV2SentimentToLegacyValue(input.sentiment),
      });
      if (data.conversationSentimentType !== undefined) {
        conversationSentimentType = mapSentimentTypeToInboxV2Sentiment(
          data.conversationSentimentType
        );
      }
    }

    if (input.isDeleted) {
      await setMessageDeletedStatus({ id: input.messageId });
    }

    if (input.isHidden !== undefined) {
      await setMessageHidden({
        id: input.messageId,
        hidden: input.isHidden,
      });
    }

    if (input.body !== undefined) {
      await setMessageText({
        id: input.messageId,
        text: input.body,
      });
    }

    return conversationSentimentType
      ? { sentimentType: conversationSentimentType }
      : {};
  },

  async createTag(input: CreateInboxV2TagInput) {
    return mapTagToInboxV2Tag(await createTag(input));
  },

  async updateTag(input: UpdateInboxV2TagInput) {
    return mapTagToInboxV2Tag(await updateInboxTag(input.id, input));
  },

  async deleteTag(tagId: string) {
    await deleteInboxTag(tagId);
  },

  async findDirectConversation(input: {
    accountId: string;
    platform?: string;
    senderPlatformId?: string;
  }) {
    if (!input.platform || !input.senderPlatformId) {
      return null;
    }
    const response = await skedInboxApi.get<{
      conversationId: string | null;
    }>("/inbox-v2/conversations/direct-message", {
      params: {
        ...input,
        platform: input.platform === "instagram" ? "IG" : "FB",
      },
    });

    return response.data.conversationId
      ? this.getConversation(response.data.conversationId)
      : null;
  },

  async getProfileComments(input: GetInboxV2ProfileCommentsInput) {
    const response = await getInboxV2ProfileComments({
      profileId: input.profileId,
      limit: input.pageSize,
      next: input.nextToken,
    });

    return {
      comments: response.comments,
      nextToken: response.nextToken ?? undefined,
    } satisfies InboxV2ProfileCommentsPage;
  },

  async getPostComments(input: GetInboxV2PostCommentsInput) {
    const response = await getInboxV2PostComments({
      conversationId: input.conversationId,
      limit: input.pageSize,
      next: input.nextToken,
    });

    return {
      comments: response.comments,
      fetchError: response.fetchError,
      nextToken: response.nextToken,
    } satisfies InboxV2PostCommentsPage;
  },

  async getPostCommentReplies(input: GetInboxV2PostCommentRepliesInput) {
    const response = await getInboxV2PostCommentReplies({
      commentId: input.commentId,
      conversationId: input.conversationId,
      limit: input.pageSize,
      next: input.nextToken,
    });

    return {
      fetchError: response.fetchError,
      replies: response.replies,
      nextToken: response.nextToken,
    } satisfies InboxV2PostCommentRepliesPage;
  },

  async findConversationByMessageId(messageId: string) {
    const { data } = await skedInboxApi.get<{ conversationId: string }>(
      `/inbox-v2/conversations/by-message/${messageId}`
    );

    return this.getConversation(data.conversationId);
  },

  async getSenderProfile(input: {
    identity: {
      accountId: string;
      conversationId?: string;
      platform: string;
      platformId: string;
      postId?: string;
      messagePlatformId?: string;
    };
    refreshReason?: import("../../types").InboxV2SenderProfileRefreshReason;
  }) {
    return getInboxV2SenderProfile(input);
  },

  async getSenderProfiles(input: {
    identities: Array<{
      accountId: string;
      conversationId?: string;
      platform: string;
      platformId: string;
      postId?: string;
      messagePlatformId?: string;
    }>;
    refreshReason?: import("../../types").InboxV2SenderProfileRefreshReason;
  }) {
    return getInboxV2SenderProfilesApi(input);
  },
};
